// platform-password-recovery: /login/reset#t=<token_hash> — הקישור מהמייל. ה-token ב-fragment (כמו 26: לא בשרת/בלוגים/ב-referrer),
// נקרא בטעינה ונמחק מההיסטוריה מיד. verify רק בשליחה (verify צורך את ה-token ומנפיק סשן — אימות בטעינה היה הופך פתיחת הקישור
// לכניסה בלי סיסמה חדשה, ו-preview של אפליקציות מייל היה שורף אותו). ok → / ; verify נכשל → /login?reason=link_expired ;
// PUT נדחה → שגיאה והטופס נשאר (אותו סשן בזיכרון, בלי קישור חדש).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { resetPasswordWithLink } from '../lib/auth.ts';
import { T } from '../lib/strings.ts';
import { Input, Label } from '../components/ui/input.tsx';
import { Button } from '../components/ui/button.tsx';
import { IconAlert } from '../components/brand/icons.tsx';
import { AuthCard } from '../components/AuthCard.tsx';

export const PASSWORD_MIN = 8;

/** t מתוך ה-fragment (#t=…). */
export function parseResetFragment(hash: string): string | null {
  return new URLSearchParams(hash.replace(/^#/, '')).get('t') || null;
}

/** בדיקה מקומית לפני verify (לא "לשרוף" את הקישור על טעות הקלדה). */
export function localPasswordError(pw: string, confirm: string): string | null {
  if (pw.length < PASSWORD_MIN) return T.reset.tooShort;
  if (pw !== confirm) return T.reset.mismatch;
  return null;
}

export function ResetPage() {
  const nav = useNavigate();
  /** ה-token נקרא פעם אחת בטעינה ונמחק מה-URL — לא נשאר בהיסטוריה, ב-location או ב-state של React Router; מוחזק ב-ref עד השליחה. */
  const token = useRef<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return; // StrictMode: קריאה אחת (ה-fragment כבר נמחק)
    started.current = true;
    token.current = parseResetFragment(window.location.hash);
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    if (!token.current) nav('/login?reason=link_expired', { replace: true });
  }, [nav]);
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const pwRef = useRef<HTMLInputElement>(null);

  return (
    <AuthCard title={T.reset.title} lead={T.reset.lead} testId="reset">
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          setErr('');
          const local = localPasswordError(pw, confirm);
          if (local) return setErr(local);
          if (!token.current) return nav('/login?reason=link_expired', { replace: true });
          setBusy(true);
          const r = await resetPasswordWithLink(token.current, pw);
          setBusy(false);
          if (r === 'ok') return nav('/', { replace: true });
          if (r === 'link_expired') return nav('/login?reason=link_expired', { replace: true });
          setErr(T.reset.rejected);
          setPw('');
          setConfirm('');
          pwRef.current?.focus();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">{T.reset.password}</Label>
          <Input id="password" ref={pwRef} type="password" dir="ltr" autoComplete="new-password" autoFocus minLength={PASSWORD_MIN} value={pw} onChange={(e) => setPw(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirm">{T.reset.confirm}</Label>
          <Input id="confirm" type="password" dir="ltr" autoComplete="new-password" minLength={PASSWORD_MIN} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        {err && (
          <p role="alert" className="m-0 flex items-center gap-2 rounded-md border border-error/35 bg-error/[0.07] px-3 py-2 text-sm font-medium text-error" data-testid="reset-error">
            <IconAlert size={16} />
            {err}
          </p>
        )}
        <Button type="submit" size="lg" className="mt-1 w-full" disabled={busy}>
          {busy ? T.reset.busy : T.reset.submit}
        </Button>
      </form>
    </AuthCard>
  );
}
